/**
 * Film detail — the screen the web layout never had.
 *
 * ## What was there before
 *
 * `/item/:id` redirected to `/watch/:id`. A card was a play button: there was no way to
 * read a synopsis, check a running time, see who is in it, or find out that the thing you
 * are half way through is the thing you are half way through — without starting the
 * stream, which on a metered subscription burns one of the account's connection slots
 * before you have decided you want it.
 *
 * The television has had this screen since it was built (`TvItemDetail`). PC and mobile
 * were the ones browsing a catalogue by guessing.
 *
 * ## Why it is not `TvItemDetail` with different CSS
 *
 * The same reason the search screens are two components: every element there is registered
 * with the spatial focus engine and laid out so four arrow presses reach all of it. Here
 * the input is a pointer, the actions can sit in a row, and the page can be as long as it
 * needs to be because it scrolls. Sharing the markup would mean the web page carrying a
 * focus registry it never uses and the television carrying a layout it cannot navigate.
 *
 * What *is* shared is everything that matters: the same endpoints, the same `MediaImage`,
 * the same tint, the same action components, and `detailPath` — so a card goes to the same
 * place on both.
 */
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  CatalogItemDto,
  RecommendationRowDto,
  WatchProgressDto,
} from '@filmodasi/shared';
import { useApi } from '../api/hooks';
import { ErrorBox, ItemCard, SkeletonRails } from '../components/ui';
import { Rail } from '../components/rows';
import { Icon } from '../components/Icon';
import { MediaImage, tintFor } from '../components/MediaImage';
import { FavoriteButton } from '../components/FavoriteButton';
import { RecommendButton } from '../components/RecommendButton';
import { PlayOnTvButton } from '../components/PlayOnTvButton';
import { maturityLabel } from '../catalog/maturity';

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

export function ItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const item = useApi<CatalogItemDto>(id ? `/api/catalog/item/${id}` : null);
  // 200 with a null body when nothing has been watched, so a miss is not an error.
  const progress = useApi<WatchProgressDto | null>(id ? `/api/progress/${id}` : null);
  /*
   * "Benzerler", from the row engine rather than from a new endpoint.
   *
   * `because` seeded on an item is already "things like this one" — it is what the
   * homepage row is built from. Reusing it means the detail page and the homepage cannot
   * disagree about what resembles what, and it cost no server work at all.
   */
  const similar = useApi<RecommendationRowDto>(
    id ? `/api/recommendations/row/because?seedId=${id}` : null,
  );

  if (item.loading && !item.data) {
    return (
      <main className="itemdetail">
        <p className="sr-only" role="status">
          Yükleniyor…
        </p>
        <div className="itemdetail__skeleton" aria-hidden />
        <SkeletonRails rows={1} />
      </main>
    );
  }
  if (item.error) return <ErrorBox message={item.error} onRetry={item.reload} />;
  if (!item.data) return null;

  const data = item.data;
  const resume = progress.data;
  const canResume = Boolean(resume && resume.positionSec > 0 && !resume.completed);
  const art = data.poster ?? data.logo;

  const meta = [
    data.year ? String(data.year) : null,
    data.durationSec ? minutes(data.durationSec) : null,
    data.rating ? `★ ${data.rating.toFixed(1)}` : null,
    // The provider's own string where there is one, the classifier's band otherwise —
    // the same rule as the hero, so a film reads the same in both places.
    data.ageRating ?? maturityLabel(data.maturity),
    data.genres.length > 0 ? data.genres.join(' · ') : null,
  ].filter((x): x is string => Boolean(x));

  return (
    <main className="itemdetail">
      <section className="itemdetail__hero" style={{ backgroundImage: tintFor(data.id) }}>
        <div className="itemdetail__media">
          <MediaImage src={art} shape="hero" seed={data.id} priority />
        </div>

        <div className="itemdetail__body">
          {/* Back to where you came from, not to a fixed screen: this page is reachable
              from Home, three libraries, search and the inbox. */}
          <button type="button" className="itemdetail__back" onClick={() => navigate(-1)}>
            <Icon name="back" size={1} />
            Geri
          </button>

          <h1 className="itemdetail__title">{data.title}</h1>

          {meta.length > 0 && (
            <p className="itemdetail__meta">
              {meta.map((part, i) => (
                <span key={i}>{part}</span>
              ))}
            </p>
          )}

          {canResume && resume && (
            <div className="itemdetail__resume">
              <div className="itemdetail__bar">
                <span style={{ width: `${Math.min(100, resume.percent)}%` }} />
              </div>
              <span className="itemdetail__resumeText">
                {minutes(resume.positionSec)} izlendi
                {/* The provider's duration is often missing, and a film with no known
                    length still has a position worth showing. */}
                {resume.durationSec != null &&
                  resume.durationSec > resume.positionSec &&
                  ` · ${minutes(resume.durationSec - resume.positionSec)} kaldı`}
              </span>
            </div>
          )}

          {data.plot && <p className="itemdetail__plot">{data.plot}</p>}

          <div className="itemdetail__actions">
            <Link to={`/watch/${data.id}`} className="itemdetail__play">
              <Icon name="play" size={1.1} />
              {canResume ? 'Devam et' : 'Oynat'}
            </Link>
            {/*
              Only where there is something to restart *from*.

              A "Baştan başla" beside a film nobody has opened is a second button that does
              exactly what the first one does, which is how a two-action row teaches people
              that one of its buttons is noise.
            */}
            {canResume && (
              <Link to={`/watch/${data.id}?restart=1`} className="itemdetail__secondary">
                Baştan başla
              </Link>
            )}
            <FavoriteButton item={data} />
            <RecommendButton itemId={data.id} />
            <PlayOnTvButton itemId={data.id} />
          </div>

          {/* Enrichment fills these in over days, so both are routinely absent and each is
              rendered independently rather than as one block that needs both. */}
          {(data.cast.length > 0 || data.director) && (
            <dl className="itemdetail__credits">
              {data.cast.length > 0 && (
                <>
                  <dt>Oyuncular</dt>
                  <dd>{data.cast.slice(0, 10).join(', ')}</dd>
                </>
              )}
              {data.director && (
                <>
                  <dt>Yönetmen</dt>
                  <dd>{data.director}</dd>
                </>
              )}
            </dl>
          )}

          {/* A caution, not a verdict — Chrome plays .mkv, Firefox does not. Stated here
              rather than only as a corner badge, because this is the screen where somebody
              is deciding whether to press play. */}
          {!data.playable && data.containerExt && (
            <p className="itemdetail__warning">
              Bu dosya <code>.{data.containerExt}</code> biçiminde ve bu tarayıcıda
              oynatılamayabilir. Televizyonda sorunsuz açılır.
            </p>
          )}
        </div>
      </section>

      {/* Dropped entirely when the recommender has nothing — a "Benzerler" heading over an
          empty rail reads as a broken screen rather than as an honest absence. */}
      {similar.data && similar.data.items.length > 0 && (
        <Rail title="Benzerler">
          {similar.data.items
            // The film itself would otherwise be the first card in its own similar row.
            .filter((other) => other.id !== data.id)
            .map((other) => (
              <ItemCard key={other.id} item={other} />
            ))}
        </Rail>
      )}
    </main>
  );
}
